import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import {
  School as SchoolIcon,
  Description as DocumentIcon,
  VideoLibrary as VideoIcon,
  Support as SupportIcon,
  TrendingUp as TrendingIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, FeatureCard } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const CaregiverResourcesPage: React.FC = () => {
  const theme = useTheme();

  const resources = [
    {
      icon: <SchoolIcon sx={{ fontSize: 40 }} />,
      title: "Training & Education",
      description: "Access comprehensive training materials, continuing education courses, and professional development resources to enhance your skills.",
      action: <Button variant="outlined" href="/training">View Training</Button>,
    },
    {
      icon: <DocumentIcon sx={{ fontSize: 40 }} />,
      title: "Documentation Tools",
      description: "Streamline your documentation with our digital tools, templates, and best practices for efficient patient care documentation.",
      action: <Button variant="outlined" href="/tools">Access Tools</Button>,
    },
    {
      icon: <VideoIcon sx={{ fontSize: 40 }} />,
      title: "Video Library",
      description: "Watch instructional videos, case studies, and expert presentations to stay current with the latest healthcare practices.",
      action: <Button variant="outlined" href="/videos">Watch Videos</Button>,
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40 }} />,
      title: "24/7 Support",
      description: "Get help whenever you need it with our dedicated support team, peer network, and online community forums.",
      action: <Button variant="outlined" href="/support">Get Support</Button>,
    },
    {
      icon: <TrendingIcon sx={{ fontSize: 40 }} />,
      title: "Marketing Tools",
      description: "Promote your services with our marketing tools, profile optimization tips, and patient acquisition strategies.",
      action: <Button variant="outlined" href="/marketing">Marketing Help</Button>,
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40 }} />,
      title: "Community Network",
      description: "Connect with other healthcare professionals, share experiences, and learn from peers in our professional community.",
      action: <Button variant="outlined" href="/community">Join Community</Button>,
    },
  ];

  const trainingCategories = [
    {
      title: "Clinical Skills",
      items: [
        "Evidence-based treatment protocols",
        "Assessment and evaluation techniques",
        "Treatment planning and documentation",
        "Patient safety and infection control",
      ],
    },
    {
      title: "Technology & Tools",
      items: [
        "Platform navigation and features",
        "Digital documentation best practices",
        "Telehealth and virtual care delivery",
        "Mobile app usage and optimization",
      ],
    },
    {
      title: "Business & Marketing",
      items: [
        "Building your practice",
        "Patient communication strategies",
        "Pricing and service optimization",
        "Professional networking and referrals",
      ],
    },
    {
      title: "Compliance & Legal",
      items: [
        "HIPAA compliance and privacy",
        "Professional liability and insurance",
        "State licensing requirements",
        "Documentation and record-keeping",
      ],
    },
  ];

  return (
    <PageLayout
      title="Caregiver Resources"
      subtitle="Access comprehensive tools, training, and support to help you succeed as a healthcare professional on our platform."
      breadcrumbs={[
        { label: "For Caregivers", href: "/for-caregivers" },
        { label: "Caregiver Resources" },
      ]}
    >
      {/* Resources Overview */}
      <ContentSection
        title="Everything You Need to Succeed"
        subtitle="Our comprehensive resource library provides everything you need to build a successful practice and deliver exceptional patient care."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          {resources.map((resource, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <FeatureCard {...resource} />
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Training Categories */}
      <ContentSection
        title="Training & Education Categories"
        subtitle="Comprehensive training programs designed to enhance your clinical skills and business success."
      >
        <Grid container spacing={4}>
          {trainingCategories.map((category, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box
                sx={{
                  p: 3,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    mb: 3,
                  }}
                >
                  {category.title}
                </Typography>
                <Box component="ul" sx={{ pl: 0, listStyle: 'none' }}>
                  {category.items.map((item, itemIndex) => (
                    <Box component="li" key={itemIndex} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.success.main,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          mr: 2,
                          mt: 0.5,
                          flexShrink: 0,
                        }}
                      >
                        ✓
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.primary,
                          lineHeight: 1.6,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Ready to Access Your Resources?"
        subtitle="Join our network of healthcare professionals and gain access to comprehensive tools and training."
        primaryButton={{
          text: "Get Started",
          href: "/auth?role=caregiver",
        }}
        secondaryButton={{
          text: "View Resources",
          href: "/resources",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
